import React, { useCallback } from 'react';
import { Dialog, DialogContent, Chip, DialogTitle, IconButton } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { useDispatch, useSelector } from 'react-redux';
import { importAssetStyle } from './style';
import Dropzone from 'react-dropzone';
import { MdClose } from 'react-icons/md';
import { useSnackbar } from 'notistack';
import { unwrapResult } from '@reduxjs/toolkit';

import { accountsSlice } from 'app/store';
import { activateLoading, deactivateLoading } from 'GeminiViewerComponent/features/globals/loadingProgressSlice';
import { importAsset } from 'features/assets/assetsTableSlice';
import { resetGroupState } from 'features/groups/groupsSlice';
import { SnackbarDismiss } from 'GeminiViewerComponent/components/SnackbarDismiss';
import { snackbarHandler } from 'GeminiViewerComponent/helpers/snackbar-handler';
import { makeFormStyles } from 'forms/styles';
import { panelStyles } from 'GeminiViewerComponent/components/styles';
import { Role } from 'helpers';

const ImportAsset = ({ openDialog, setOpenDialog }) => {
    const dispatch = useDispatch();
    const theme = useSelector(selectDefaultTheme);
    const activeUser = useSelector(accountsSlice.selectActiveUser);
    const readOnlyMode = activeUser?.role === Role.User ? true : false;
    const importClasses = importAssetStyle(theme);
    const classes = makeFormStyles(theme);
    const panelClasses = panelStyles(theme);
    const { enqueueSnackbar } = useSnackbar();

    const onDrop = useCallback(({ file, setFieldValue }) => {
        setFieldValue('import_file', file);

        const reader = new FileReader();

        reader.onabort = () => console.log('file reading was aborted');
        reader.onerror = () => console.log('file reading has failed');
        reader.onload = (e) => {};
        reader.readAsDataURL(file);
    }, []);

    const handleSubmit = async (values) => {
        dispatch(activateLoading({ showProgress: true }));
        try {
            let resultAction = await dispatch(
                importAsset({
                    ...values,
                })
            );
            dispatch(deactivateLoading());
            setOpenDialog(false);
            dispatch(resetGroupState());
            unwrapResult(resultAction);
            const { message, variant } = snackbarHandler(
                resultAction.meta.requestStatus,
                'Import',
                resultAction.payload.message
            );
            enqueueSnackbar(message, {
                action: (key) => <SnackbarDismiss snackItem={key} />,
                variant: variant,
            });
        } catch (err) {
            dispatch(deactivateLoading());
            setOpenDialog(false);
            enqueueSnackbar('Import Failed' + (err?.message ? `: ${err?.message}` : ''), {
                action: (key) => <SnackbarDismiss key={key} />,
                variant: 'error',
            });
        }
    };

    return (
        <Dialog fullScreen={false} open={openDialog} disableEnforceFocus={true} onClose={() => setOpenDialog(false)}>
            <DialogTitle sx={{ fontSize: '20px', padding: '10px 24px' }}>
                Import Asset
                <IconButton
                    aria-label="close"
                    onClick={() => setOpenDialog(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 6,
                        color: '#000',
                    }}
                    size="large"
                >
                    <MdClose className="react-icon" />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Formik
                    initialValues={{ import_file: null }}
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}
                >
                    {({ values }) => (
                        <Form className={classes.form}>
                            <Field className={classes.input} name="import_file" type="file">
                                {({ field: { value }, form: { setFieldValue } }) => (
                                    <div className={panelClasses.dropzoneContainer}>
                                        <Dropzone
                                            accept={'.json'}
                                            maxFiles={1}
                                            maxSize={31457280}
                                            multiple={false}
                                            onDropAccepted={(files) => {
                                                onDrop({
                                                    file: files[0],
                                                    setFieldValue: setFieldValue,
                                                });
                                            }}
                                            noClick={readOnlyMode}
                                        >
                                            {({ getRootProps, getInputProps }) => (
                                                <div
                                                    {...getRootProps()}
                                                    className={panelClasses.dropzoneArea}
                                                    style={{
                                                        backgroundRepeat: 'no-repeat',
                                                        backgroundSize: 'contain',
                                                        backgroundPosition: 'center center',
                                                    }}
                                                >
                                                    <input {...getInputProps()} />
                                                    <div className={panelClasses.dropzoneText}>
                                                        {values.import_file
                                                            ? 'Drop replacement .json import file here or click to browse'
                                                            : 'Drop .json import file here or click to browse.'}
                                                        <br />
                                                        <br />
                                                        (.json import files are created using the Export Data option.)
                                                    </div>
                                                </div>
                                            )}
                                        </Dropzone>
                                        <div className={importClasses.previewContainer}>
                                            {value && Object.keys(value).length > 0 && (
                                                <Chip
                                                    className={importClasses.previewUploadFileName}
                                                    color="primary"
                                                    size="small"
                                                    // deleteIcon={
                                                    //     <MdClose className="react-icon" />
                                                    // }
                                                    onDelete={() => {
                                                        if (!readOnlyMode) {
                                                            setFieldValue('import_file', null);
                                                        }
                                                    }}
                                                    label={value?.name}
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}
                            </Field>
                            {readOnlyMode !== true && (
                                <button
                                    className={classes.submit}
                                    type="submit"
                                    disabled={Boolean(!values?.import_file)}
                                >
                                    Submit
                                </button>
                            )}
                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    );
};

export default ImportAsset;
